<template>
  <v-container fluid>
    <v-layout row wrap>
      <v-flex class="d-flex justify-space-between mb-3 align-center" xs12>
        <h3 class="font-weight-bold">{{ routeLabel }}</h3>
        <div class="text-xs-right d-flex align-center">
          <v-btn color="blue darken-3" dark small class="mr-2" @click="handleExport" :loading="exporting">
            <v-icon left small>download</v-icon>
            Export
          </v-btn>
          <refresh-button :click-function="fetchData" />
        </div>
      </v-flex>
      <v-flex xs12 class="mb-3">
        <v-layout row wrap align-center>
          <v-flex xs12 sm6 class="pr-2">
            <v-card class="pa-3">
              <div class="caption grey--text">SUBMISSIONS {{ DateToShow }}</div>
              <div class="display-1 font-weight-bold mt-1">
                <template v-if="statsLoading">
                  <div class="skeleton-line" style="width: 80px; height: 36px; border-radius: 6px;"></div>
                </template>
                <span v-else>{{ stats.submissions_today }}</span>
              </div>
            </v-card>
          </v-flex>

          <v-flex xs12 sm6>
            <v-card class="pa-3">
              <div class="d-flex justify-space-between align-center">
                <div class="caption grey--text">REVENUE {{ DateToShow }}</div>
              </div>
              <div class="display-1 font-weight-bold mt-1">
                <template v-if="statsLoading">
                  <div class="skeleton-line" style="width: 140px; height: 36px; border-radius: 6px;"></div>
                </template>
                <span v-else>Rs. {{ Number(stats.revenue_today).toLocaleString() }}</span>
              </div>
            </v-card>
          </v-flex>
        </v-layout>
        <v-layout class="pt-2" row wrap align-center>
          <v-flex xs12 sm6 class="pr-2">
            <v-card
                class="pa-3"
                :class="{ 'active-card-green': filters.within_standard_time === 'true' }"
                style="cursor: pointer;"
                @click="filters.within_standard_time = filters.within_standard_time === 'true' ? null : 'true'; fetchData()"
            >
              <div class="caption grey--text">Within Standard Time Frame {{ DateToShow }}</div>
              <div class="mt-2 d-flex align-center" style="gap: 10px;">
                <template v-if="statsLoading">
                  <div class="skeleton-line" style="width: 80px; height: 32px; border-radius: 6px;"></div>
                </template>
                <template v-else>
                  <span class="display-1 font-weight-bold green--text">{{ stats.within_standard_time }}</span>
                  <span class="percent-pill percent-pill--green">{{ withinAndExceededPercent[0] }}%</span>
                </template>
              </div>
              <div class="progress-track mt-2" v-if="!statsLoading">
                <div class="progress-fill progress-fill--green" :style="{ width: withinAndExceededPercent[0] + '%' }"></div>
              </div>
              <div v-if="filters.within_standard_time === 'true'" class="caption green--text mt-2 d-flex align-center">
                <v-icon x-small color="green" class="mr-1">check_circle</v-icon> Filter active
              </div>
            </v-card>
          </v-flex>

          <v-flex xs12 sm6>
            <v-card
                class="pa-3"
                :class="{ 'active-card-red': filters.within_standard_time === 'false' }"
                style="cursor: pointer;"
                @click="filters.within_standard_time = filters.within_standard_time === 'false' ? null : 'false'; fetchData()"
            >
              <div class="caption grey--text">Standard Time Frame Exceeded {{ DateToShow }}</div>
              <div class="mt-2 d-flex align-center" style="gap: 10px;">
                <template v-if="statsLoading">
                  <div class="skeleton-line" style="width: 140px; height: 32px; border-radius: 6px;"></div>
                </template>
                <template v-else>
                  <span class="display-1 font-weight-bold red--text">{{ stats.exceeded_standard_time }}</span>
                  <span class="percent-pill percent-pill--red">{{ withinAndExceededPercent[1] }}%</span>
                </template>
              </div>
              <div class="progress-track mt-2"  v-if="!statsLoading">
                <div class="progress-fill progress-fill--red" :style="{ width: withinAndExceededPercent[1] + '%' }"></div>
              </div>
              <div v-if="filters.within_standard_time === 'false'" class="caption red--text mt-2 d-flex align-center">
                <v-icon x-small color="red" class="mr-1">cancel</v-icon> Filter active
              </div>
            </v-card>
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex xs12>
        <v-card>
          <v-layout class="py-2 px-3" row wrap align-center>
            <v-flex xs12 sm4 class="pr-2">
              <v-flex class="d-flex align-center">
                <custom-date-range-picker
                    v-model="filters.date_range"></custom-date-range-picker>
              </v-flex>
            </v-flex>
            <!--            <v-flex xs12 sm4>-->
            <!--              <v-autocomplete-->
            <!--                  :elevation="0"-->
            <!--                  flat-->
            <!--                  v-model="filters.office_id"-->
            <!--                  :items="offices"-->
            <!--                  item-text="name"-->
            <!--                  item-value="id"-->
            <!--                  label="Filter by office"-->
            <!--                  prepend-inner-icon="filter_list"-->
            <!--                  solo-->
            <!--                  hide-details-->
            <!--                  clearable-->
            <!--                  @change="fetchData"-->
            <!--              />-->
            <!--            </v-flex>-->
            <v-flex xs12 sm4>
              <v-autocomplete
                  :elevation="0"
                  flat
                  v-model="filters.section_type"
                  :items="sectionTypes"
                  item-text="text"
                  item-value="value"
                  label="Filter by Section"
                  prepend-inner-icon="filter_list"
                  solo
                  hide-details
                  clearable
                  @change="() => { fetchStats(); fetchData(); }"
              />
            </v-flex>
            <v-flex xs12 sm4>
              <v-autocomplete
                  :elevation="0"
                  flat
                  v-model="filters.within_standard_time"
                  :items="standardTime"
                  item-text="text"
                  item-value="value"
                  label="Filter by Time Taken"
                  prepend-inner-icon="filter_list"
                  solo
                  hide-details
                  clearable
                  @change="fetchData"
              />
            </v-flex>
            <v-flex xs12 sm4>
              <v-autocomplete
                  :elevation="0"
                  flat
                  v-model="filters.department_type"
                  :items="user_departments"
                  item-text="name"
                  item-value="id"
                  label="Filter by Department"
                  prepend-inner-icon="filter_list"
                  solo
                  hide-details
                  clearable
                  @change="() => { fetchStats(); fetchData(); }"
              />
            </v-flex>
          </v-layout>
          <v-divider />

          <v-card-text class="pa-0">
            <v-divider />
            <div class="px-3 py-2 d-flex align-center flex-wrap" style="background-color: #f5f5f5; gap: 8px;">

              <!-- ALL -->
              <v-btn
                  small
                  :color="!filters.for_trekkers && !filters.ext_con && !filters.for_visa && !filters.for_certification && !filters.for_visaTransfer ? 'blue darken-3' : ''"
                  :dark="!filters.for_trekkers && !filters.ext_con && !filters.for_visa && !filters.for_certification && !filters.for_visaTransfer"
                  depressed
                  :outlined="filters.for_trekkers || filters.ext_con || filters.for_visa || filters.for_certification || filters.for_visaTransfer"
                  @click="selectAll"
              >
                All
                <template v-if="statsLoading">
                  <div class="chip-skeleton ml-2"></div>
                </template>
                <v-chip v-else x-small class="ml-2"
                        :color="!filters.for_trekkers && !filters.ext_con && !filters.for_visa && !filters.for_certification && !filters.for_visaTransfer ? 'white' : 'blue darken-3'"
                        :text-color="!filters.for_trekkers && !filters.ext_con && !filters.for_visa && !filters.for_certification && !filters.for_visaTransfer ? 'blue darken-3' : 'white'">
                  {{ stats.submissions_today }}
                </v-chip>
              </v-btn>

              <!-- VISA SECTION -->
              <v-btn
                  small
                  :color="filters.for_visa ? 'blue darken-3' : ''"
                  :dark="filters.for_visa"
                  depressed
                  :outlined="!filters.for_visa"
                  @click="toggleVisa"
              >
                Visa Section
                <template v-if="statsLoading">
                  <div class="chip-skeleton ml-2"></div>
                </template>
                <v-chip v-else x-small class="ml-2"
                        :color="filters.for_visa ? 'white' : 'blue darken-3'"
                        :text-color="filters.for_visa ? 'blue darken-3' : 'white'">
                  {{ stats.visa_count }}
                </v-chip>
              </v-btn>

              <!-- TREKKING SECTION -->
              <v-btn
                  small
                  :color="filters.for_trekkers ? 'blue darken-3' : ''"
                  :dark="filters.for_trekkers"
                  depressed
                  :outlined="!filters.for_trekkers"
                  @click="toggleTrekkers"
              >
                Trekking Section
                <template v-if="statsLoading">
                  <div class="chip-skeleton ml-2"></div>
                </template>
                <v-chip v-else x-small class="ml-2"
                        :color="filters.for_trekkers ? 'white' : 'blue darken-3'"
                        :text-color="filters.for_trekkers ? 'blue darken-3' : 'white'">
                  {{ stats.trekking_count }}
                </v-chip>
              </v-btn>

              <!-- EXTENSION DESK -->
              <v-btn
                  small
                  :color="filters.ext_con ? 'blue darken-3' : ''"
                  :dark="filters.ext_con"
                  depressed
                  :outlined="!filters.ext_con"
                  @click="toggleExtCon"
              >
                Extension Desk
                <template v-if="statsLoading">
                  <div class="chip-skeleton ml-2"></div>
                </template>
                <v-chip v-else x-small class="ml-2"
                        :color="filters.ext_con ? 'white' : 'blue darken-3'"
                        :text-color="filters.ext_con ? 'blue darken-3' : 'white'">
                  {{ stats.ext_con_count }}
                </v-chip>
              </v-btn>
              <v-btn
                  small
                  :color="filters.for_certification ? 'blue darken-3' : ''"
                  :dark="filters.for_certification"
                  depressed
                  :outlined="!filters.for_certification"
                  @click="toggleCertification"
              >
                Certification Desk
                <template v-if="statsLoading">
                  <div class="chip-skeleton ml-2"></div>
                </template>
                <v-chip v-else x-small class="ml-2"
                        :color="filters.for_certification ? 'white' : 'blue darken-3'"
                        :text-color="filters.for_certification ? 'blue darken-3' : 'white'">
                  {{ stats.certification_count }}
                </v-chip>
              </v-btn>
              <v-btn
                  small
                  :color="filters.for_visaTransfer ? 'blue darken-3' : ''"
                  :dark="filters.for_visaTransfer"
                  depressed
                  :outlined="!filters.for_visaTransfer"
                  @click="toggleVisaTransfer"
              >
                Transfer Desk
                <template v-if="statsLoading">
                  <div class="chip-skeleton ml-2"></div>
                </template>
                <v-chip v-else x-small class="ml-2"
                        :color="filters.for_visaTransfer ? 'white' : 'blue darken-3'"
                        :text-color="filters.for_visaTransfer ? 'blue darken-3' : 'white'">
                  {{ stats.visa_transfer_count }}
                </v-chip>
              </v-btn>

            </div>            <v-divider />

            <v-data-table
                :headers="headers"
                :items="rows"
                :loading="loading"
                :options.sync="pagination"
                :server-items-length="serverTotal"
                :footer-props="footerProps"
                item-key="id"
            >
              <template v-slot:item.s_no="{ index }">
                <td>{{ (pagination.page - 1) * pagination.itemsPerPage + index + 1 }}</td>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="exportDialog" persistent width="500">
      <v-card>
        <v-toolbar color="blue darken-3" dark>
          <v-toolbar-title>Export Options</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="exportDialog = false"><v-icon>close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-form ref="exportFormRef" v-model="exportValid" lazy-validation>
            <v-text-field v-model="exportName" label="Name" outlined />
            <v-text-field v-model="exportRows" :rules="[rules.required]" label="Number of rows to export *" type="number" outlined />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="red lighten-1" class="white--text" @click="exportDialog = false">Cancel</v-btn>
          <v-btn color="blue darken-3" class="white--text" :loading="exporting" @click="handleExportClicked">Export</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Http from "axios";
import { VERSIONED_API_URL } from "../../config/config";
import RefreshButton from "@/components/RefreshButton";
import customDateRangePicker from "../../components/CustomDateRangePicker.vue";
import {fetchDynamicValues} from "../../helpers/dynamicValueHelper";

// const TRACK_STATUS = {
//   VERIFIED: 47,
//   VOUCHER_GENERATED: 48,
//   CHECK_PAYMENT: 1,
//   VISA_NUMBER_PRINTED: 74,
// };

export default {
  name: "DigitalTimeCard",
  components: { RefreshButton,customDateRangePicker },

  data() {
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      rows: [], loading: false, serverTotal: 0,
      filters: {
        date_range: { start: currentDate, end: currentDate },
        country: [],
        office_id: null,
        visa_type_id: null,
        section_type: null,
        for_trekkers: false,
        for_visa: false,
        ext_con: false,
        for_certification: false,
        within_standard_time: null,
        for_visaTransfer: null,
        department_type: null,
      },
      dateMenu: false, dateRangeValues: [currentDate, currentDate],
      pagination: { page: 1, itemsPerPage: 25, sortBy: ["created_at"], sortDesc: [true] },
      footerProps: { "items-per-page-options": [10, 25, 50, 100,{text: 'All', 'value': -1}] },
      exportDialog: false, exporting: false, exportValid: true, exportName: "", exportRows: 500,
      rules: { required: (v) => !!v || "This field is required" },
      sectionTypes: [
        { text: 'Tourist Section',     value: 'Tourist Section' },
        { text: 'Business Section',    value: 'Business Section' },
        { text: 'Non-Tourist Section', value: 'Non-Tourist Section' },
        {text: 'Trekking Section', value: 'Trekking Section'},
        {text: 'Certification Section', value: 'Certification Section'},
        {text:'Transfer Section', value: 'Transfer Section'},
      ],
      standardTime: [
        { text: 'Within Standard Time Frame',   value: 'true'  },
        { text: 'Standard Time Frame Exceeded', value: 'false' },
      ],
      user_departments: [],
      stats: {
        submissions_today: 0,
        revenue_today: 0,
        visa_count: 0,
        trekking_count: 0,
        ext_con_count: 0,
        certification_count: 0,
        visa_transfer_count: 0,
        exceeded_standard_time: 0,
        within_standard_time: 0,
      },
      statsLoading: false,
      headers: [
        { text: "S.N.",           value: "s_no",            sortable: false },
        { text: "Section",      value: "section", sortable: false },
        { text: "Visa Type",    value: "visa_type", sortable: false },
        { text: "Service Type", value: "service_type", sortable: false },
        { text: "Submission No.", value: "submission_no", sortable: false },
        { text: "Verification TimeStamp", value: "verification_timestamp", sortable: true },
        { text: "Voucher Generation", value: "voucher_generation", sortable: true },
        { text: "Check Payment", value: "check_payment", sortable: true },
        { text: "Issued Date", value: "visa_sticker_printed", sortable: true },
        { text: "Standard Time", value: "standard_time_for_processing", sortable: false },
        { text: "Total Time", value: "total_time", sortable: false },
        { text: "Revenue Generated", value: "revenue_generated", sortable: false },
      ],
    };
  },

  computed: {
    ...mapGetters({ countries: "CountryList/all", offices: "OfficeList/offices", visa_types: "VisaTypeList/all"}),
    routeLabel() { return (this.$route && this.$route.meta && this.$route.meta.label) || "Digital Time Card"; },
    formattedDateRange() {
      const { start, end } = this.filters.date_range || {};
      if (start && end) return `${start} ~ ${end}`;
      return start || "";
    },
    DateToShow() {
      const currentDate = new Date().toISOString().split("T")[0];
      if (this.filters.date_range) {
        const { start, end } = this.filters.date_range;
        if (start === currentDate && end === currentDate) {
          return 'Today';
        }
        return `from ${start} to ${end}`;
      }
      return 'Today';
    },
    totalStat(){
      return this.visaTotal  + this.stats.trekking_count;
    },
    visaTotal(){
      return this.stats.visa_count - this.stats.ext_con_count
    },
    withinAndExceededPercent() {
      const within = this.stats.within_standard_time || 0;
      const exceeded = this.stats.exceeded_standard_time || 0;
      const total = within + exceeded;

      if (!total) return [0, 0];

      return [
        Math.round((within / total) * 100),
        Math.round((exceeded / total) * 100),
      ];
    }
  },
  watch: {
    pagination: { deep: true, handler() { this.fetchData(); } },
    'filters.date_range': {
      deep: true,
      handler() {
        this.fetchStats();
        this.fetchData();
      }
    },
  },

  methods: {
    ...mapActions({ fetchCountries: "CountryList/fetchCountries", fetchOffices: "OfficeList/fetchData" }),

    buildParams() {
      const { page, itemsPerPage, sortBy, sortDesc } = this.pagination;
      const { start, end } = this.filters.date_range || {};
      const params = { page, per_page: itemsPerPage, sort_by: (sortBy && sortBy[0]) || "created_at", descending: (sortDesc && sortDesc[0] !== undefined ? sortDesc[0] : true), paginate: true };
      if (start) params.start_date = start;
      if (end)   params.end_date   = end;
      if ((this.filters.country && this.filters.country.length)) params.country   = this.filters.country;
      if (this.filters.office_id) params.office_id = this.filters.office_id;
      if (this.filters.visa_type_id != null) {
        params.visa_type_id = this.filters.visa_type_id;
      }
      if (this.filters.section_type) params.section_type = this.filters.section_type;
      if (this.filters.for_trekkers) params.for_trekkers = 1;
      if (this.filters.for_visa) params.for_visa = 1;
      if(this.filters.ext_con){
        params.ext_con = 1;
        params.for_visa = 1;
      }
      if(this.filters.for_certification) params.for_certification = 1;
      if(this.filters.for_visaTransfer) params.for_visaTransfer = 1;
      if(this.filters.within_standard_time !== null) params.within_standard_time = this.filters.within_standard_time;
      if(this.filters.department_type) params.department_type = this.filters.department_type;
      return params;
    },
    selectAll(fetchData = true) {
      this.filters.for_trekkers = false;
      this.filters.for_visa     = false;
      this.filters.ext_con      = false;
      this.filters.for_certification = false;
      this.filters.for_visaTransfer = false;
      if (fetchData) this.fetchData();
    },
    toggleTrekkers() {
      this.filters.for_trekkers = !this.filters.for_trekkers;
      if (this.filters.for_trekkers) this.filters.ext_con = this.filters.for_visa = this.filters.for_visaTransfer = this.filters.for_certification = false;
      this.fetchData();
    },
    toggleVisa() {
      this.filters.for_visa     = !this.filters.for_visa;
      if (this.filters.for_visa) {
        this.filters.for_trekkers = false;
        this.filters.ext_con      = false;
        this.filters.for_certification = false;
        this.filters.for_visaTransfer = false;
      }
      this.fetchData();
    },
    toggleExtCon() {
      this.filters.ext_con = !this.filters.ext_con;
      if (this.filters.ext_con) this.filters.for_trekkers = this.filters.for_visa = this.filters.for_certification = this.filters.for_visaTransfer = false;
      this.fetchData();
    },
    toggleCertification() {
      this.filters.for_certification = !this.filters.for_certification;
      if (this.filters.for_certification) this.filters.for_trekkers = this.filters.for_visa = this.filters.ext_con = false;
      this.fetchData();
    },
    toggleVisaTransfer(){
      this.filters.for_visaTransfer = !this.filters.for_visaTransfer;
      if(this.filters.for_visaTransfer){
        this.filters.for_trekkers = false;
        this.filters.ext_con      = false;
        this.filters.for_certification = false;
        this.filters.for_visa = false;
      }
      this.fetchData();
    },
    fetchStats() {
      this.statsLoading = true;
      this.selectAll(false);
      Http.get(`${VERSIONED_API_URL}/summary-report-data/digital-time-card`, {
        params: { ...this.buildParams(), get_stat: 1 }
      })
          .then((res) => {
            if (res.data && res.data.stats) {
              this.stats = res.data.stats;
            }
          })
          .catch((err) => console.error(err))
          .finally(() => { this.statsLoading = false; });
    },
    fetchData() {
      this.loading = true;
      Http.get(`${VERSIONED_API_URL}/summary-report-data/digital-time-card`, { params: this.buildParams() })
          .then((res) => {
            const sourceRows = (res.data && res.data.data !== undefined) ? res.data.data : res.data;
            this.rows = Array.isArray(sourceRows) ? sourceRows : [];  // ← no mapping needed
            this.serverTotal = (res.data && res.data.meta && res.data.meta.total !== undefined)
                ? res.data.meta.total
                : (res.data && res.data.total !== undefined)
                    ? res.data.total
                    : this.rows.length;
          })
          .catch((err) => console.error(err))
          .finally(() => { this.loading = false; });
    },

    handleExport() { this.exportDialog = true; },

    handleExportClicked() {
      if (!this.$refs.exportFormRef.validate()) return;
      this.exporting = true;
      Http.post(
          `${VERSIONED_API_URL}/summary-report-data/digital-time-card/export`,
          { ...this.buildParams(), exportName: this.exportName, exportRows: this.exportRows }
      )
          .then((res) => {
            if (res.data && res.data.url) {
              window.open(res.data.url, "_blank");
              // const link = document.createElement('a');
              // link.href = res.data.url;
              // link.setAttribute('download', `${this.exportName || 'digital-time-card'}.xlsx`);
              // document.body.appendChild(link);
              // link.click();
              // link.remove();
              this.exportDialog = false;
            }
          })
          .catch((err) => console.error(err))
          .finally(() => { this.exporting = false; });
    },
  },

  mounted() {
    if (this.$store.state.CountryList && this.$store.state.CountryList.all && this.$store.state.CountryList.all.length === 0) this.fetchCountries();
    this.$store.dispatch('VisaTypeList/fetchAll');
    this.fetchOffices();
    this.fetchStats();
    fetchDynamicValues('UserSection').then((res) => {
      this.user_departments = res;
    })
  },
};
</script>

<style scoped>
.font-weight-bold td { font-weight: 700; }
.skeleton-line {
  background: linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.chip-skeleton {
  display: inline-block;
  width: 28px;
  height: 18px;
  border-radius: 9px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  vertical-align: middle;
}
.percent-pill {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 99px;
}
.percent-pill--green { background: #EAF3DE; color: #27500A; }
.percent-pill--red   { background: #FCEBEB; color: #791F1F; }

.progress-track {
  width: 100%;
  height: 6px;
  background: #f0f0f0;
  border-radius: 99px;
  overflow: hidden;
}
.progress-fill { height: 100%; border-radius: 99px; transition: width 0.4s ease; }
.progress-fill--green { background: #4caf50; }
.progress-fill--red   { background: #f44336; }

.active-card-green { border: 1.5px solid #4caf50 !important; }
.active-card-red   { border: 1.5px solid #f44336 !important; }
@keyframes shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>